import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../services/auth.service';
import { MerchantType } from 'src/app/models/onboarding.model';
import { OnboardingStateService } from 'src/app/services/onboarding-state.service';
import { DashboardFixedComponent } from './dashboard-fixed/dashboard-fixed.component';
import { DashboardItinerantComponent } from './dashboard-itinerant/dashboard-itinerant.component';

@Component({
  selector: 'app-dashboard',
  imports: [DashboardFixedComponent, DashboardItinerantComponent],
  template: `
    @if (merchantType === 'fixed') {
      <app-dashboard-fixed></app-dashboard-fixed>
    }
    @if (merchantType === 'itinerant') {
      <app-dashboard-itinerant></app-dashboard-itinerant>
    }

    <!-- Loading state -->
    @if (isLoading) {
      <div class="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div class="text-center">
          <div
            class="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"
          ></div>
          <p class="text-gray-600 dark:text-gray-400">Chargement du tableau de bord...</p>
        </div>
      </div>
    }
  `,
})
export class DashboardComponent implements OnInit {
  merchantType: MerchantType | null = null;
  isLoading = true;
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  constructor(
    private authService: AuthService,
    private onboardingStateService: OnboardingStateService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.onboardingStateService.setCurrentStep(1);
    this.loadMerchantType();
  }

  private loadMerchantType(): void {
    // 1️⃣ Onboarding state signal (synchrone)
    this.merchantType = this.onboardingStateService.getMerchantType();

    // 2️⃣ User en sessionStorage (synchrone) — disponible dès le login
    if (!this.merchantType) {
      const cached = this.authService.getCurrentUser();
      this.merchantType = (cached?.merchantType as MerchantType) ?? null;
    }

    // 3️⃣ Données en cache suffisantes → afficher immédiatement
    if (this.merchantType) {
      this.finalize();
      return;
    }

    // 4️⃣ Fallback réseau uniquement si aucune donnée locale
    this.authService
      .getMe()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const user = response?.user;

          if (user?.onboardingCompleted === false) {
            this.router.navigate(['/onboarding/step/1']);
            return;
          }

          this.merchantType = user?.merchantType as MerchantType;

          if (!this.merchantType) {
            this.router.navigate(['/onboarding/step/1']);
            return;
          }

          this.finalize();
          this.cdr.markForCheck();
        },
        error: () => {
          this.router.navigate(['/login']);
        },
      });
  }

  private finalize(): void {
    if (
      this.merchantType !== MerchantType.FIXED &&
      this.merchantType !== MerchantType.ITINERANT
    ) {
      this.router.navigate(['/onboarding/step/1']);
      return;
    }
    this.isLoading = false;
    this.cdr.markForCheck();
  }
}
